export function useStream(setGuestChat) {
  function ensureStreamingBotMessage(stepId) {
    let started = false;
    let streamTimestamp = null;

    return function append(textChunk) {
      const chunk = textChunk || '';

      setGuestChat((prev) => {
        const msgs = [...(prev.messages || [])];

        let streamIdx = msgs.findIndex((m) => m._isStreaming === true);
        if (!started) {
          started = true;
          streamTimestamp = new Date().toISOString();

          let lastLoadingIdx = -1;
          for (let i = msgs.length - 1; i >= 0; i--) {
            if (msgs[i] && msgs[i].isLoading) {
              lastLoadingIdx = i;
              break;
            }
          }

          const streamShell = {
            role: 'bot',
            _isStreaming: true,
            dateCreated: streamTimestamp,
            stepId: stepId || 'response-ai',
            content: [
              {
                id: 'stream-message-1',
                stepId: stepId || 'response-ai',
                type: 'Message',
                value: '',
                dateCreated: streamTimestamp,
              },
            ],
          };

          if (lastLoadingIdx !== -1) {
            msgs.splice(lastLoadingIdx, 1, streamShell);
            streamIdx = lastLoadingIdx;
          } else if (streamIdx === -1) {
            msgs.push(streamShell);
            streamIdx = msgs.length - 1;
          }
        }

        if (streamIdx === -1) return prev;

        const content = [...msgs[streamIdx].content];
        const first = content[0] || {
          id: 'stream-message-1',
          stepId: stepId || 'response-ai',
          type: 'Message',
          value: '',
          dateCreated: streamTimestamp || new Date().toISOString(),
        };

        content[0] = { ...first, value: (first.value || '') + chunk };
        msgs[streamIdx] = { ...msgs[streamIdx], content };

        return { ...prev, messages: msgs };
      });
    };
  }

  function finalizeStreamingBotMessage() {
    setGuestChat((prev) => {
      const msgs = [...(prev.messages || [])];
      const streamingIdx = msgs.findIndex((m) => m._isStreaming === true);

      if (streamingIdx !== -1) {
        const done = { ...msgs[streamingIdx] };
        delete done._isStreaming;
        msgs[streamingIdx] = done;
        return { ...prev, messages: msgs };
      }

      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i] && msgs[i].isLoading) {
          msgs.splice(i, 1);
          break;
        }
      }
      return { ...prev, messages: msgs };
    });
  }

  function streamText(
    url,
    { onChunk, onDone, onError, timeoutMs = 120000 } = {}
  ) {
    const controller = new AbortController();
    const decoder = new TextDecoder();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    (async () => {
      try {
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            Accept: 'text/event-stream',
            'Cache-Control': 'no-store',
            Pragma: 'no-cache',
          },
          cache: 'no-store',
          signal: controller.signal,
        });

        if (!resp.ok || !resp.body) {
          const t = await resp.text().catch(() => '');
          throw new Error(t || 'Stream failed');
        }

        const ct = (resp.headers.get('content-type') || '').toLowerCase();
        const isSSE = ct.includes('text/event-stream');
        const reader = resp.body.getReader();

        if (!isSSE) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            if (controller.signal.aborted) return;
            const text = decoder.decode(value, { stream: true });
            if (text && onChunk) onChunk(text);
          }
          if (!controller.signal.aborted) onDone && onDone();
          return;
        }

        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (controller.signal.aborted) return;

          buffer += decoder.decode(value, { stream: true });

          let split;
          while ((split = buffer.indexOf('\n\n')) !== -1) {
            const ev = buffer.slice(0, split);
            buffer = buffer.slice(split + 2);

            const payload = ev
              .split(/\r?\n/)
              .filter((l) => l.startsWith('data:'))
              .map((l) => l.slice(5).trimStart())
              .join('\n');

            if (!payload) continue;
            if (payload === '[DONE]') {
              if (!controller.signal.aborted) onDone && onDone();
              return;
            }

            let out = payload;
            try {
              const obj = JSON.parse(payload);
              out = obj?.delta ?? obj?.text ?? obj?.message ?? payload;
            } catch {}
            if (onChunk) onChunk(out);
          }
        }

        if (buffer.trim() && !controller.signal.aborted)
          onChunk && onChunk(buffer.trim());
        if (!controller.signal.aborted) onDone && onDone();
      } catch (e) {
        if ((e && e.name === 'AbortError') || controller.signal.aborted) return;
        onError && onError(e);
      } finally {
        clearTimeout(timeout);
      }
    })();

    // return () => controller.abort();
  }

  return { ensureStreamingBotMessage, finalizeStreamingBotMessage, streamText };
}
